import { ConfigurationData } from "./configuration.service"

export interface AccountData {
  user: {
    record_id: string
    my_iko_id: string
    display_name: string
    language: string
    role: string
  }
  configurations: ConfigurationData[]
}

export class AccountService {
  private static csrfToken: string | null = null

  /**
   * Clear authentication data
   */
  static clearAuth(): void {
    this.csrfToken = null
    // Note: HTTP-only cookies are cleared server-side during logout
  }

  /**
   * Make authenticated request using HTTP-only session cookies
   */
  static async makeSecureRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const secureOptions: RequestInit = {
      ...options,
      credentials: "include", // Automatically includes HTTP-only cookies
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    }

    const response = await fetch(url, secureOptions)

    // Clear auth data on authentication errors
    if (response.status === 401 || response.status === 403) {
      this.clearAuth()
    }

    return response
  }

  /**
   * Get account data using HTTP-only session cookies
   */
  static async getAccountData(): Promise<AccountData | null> {
    try {
      const response = await this.makeSecureRequest("/api/account", {
        method: "GET",
      })

      if (response.ok) {
        const accountData = await response.json()
        return accountData
      }

      return null
    } catch (error) {
      console.error("Failed to fetch account data:", error)
      return null
    }
  }

  /**
   * Logout user
   */
  static async logout(): Promise<boolean> {
    try {
      const response = await this.makeSecureRequest("/api/logout", {
        method: "POST",
      })
      
      this.clearAuth()
      return response.ok
    } catch (error) {
      console.error("Logout error:", error)
      this.clearAuth()
      return false
    }
  }

  /**
   * Check if user is authenticated (synchronous check of token presence)
   */
  static async isAuthenticated(): Promise<boolean> {
    const accountData = await this.getAccountData()
    return accountData !== null && accountData.user.my_iko_id !== undefined
  }

  /**
   * Send out the monthly reports
   */
  static async sendOutReports(): Promise<boolean> {
    try {
      const csrfToken = await AccountService.getCsrfToken()
      if (!csrfToken) return false
      
      const response = await AccountService.makeSecureRequest(
        "/api/admin/send-reports",
        {
          method: "POST",
          body: JSON.stringify({ csrfToken }),
        }
      )
      if (response.ok) {
        const data = await response.json()
        return data.success
      }
    } catch (error) {
      console.error("Failed to send reports:", error)
      return false
    }
    return false
  }

  /**
   * Get CSRF token
   */
  static async getCsrfToken(): Promise<string | null> {
    if (this.csrfToken) return this.csrfToken

    try {
      const response = await this.makeSecureRequest("/api/csrf-token", {
        method: "GET",
      })

      if (response.ok) {
        const responseData = await response.json()
        this.csrfToken = responseData.csrfToken
        return this.csrfToken
      } else {
        console.error("Failed to fetch CSRF token, status:", response.status)
        return null
      }
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error)
      return null
    }
  }
}
